import { create as createBraintreeClient } from 'braintree-web/client';
import { create as createApplePayClient } from 'braintree-web/apple-pay';
import { create as createGooglePayClient } from 'braintree-web/google-payment';
import { create as createPayPalClient } from 'braintree-web/paypal-checkout';
import { create as createVenmoPayClient } from 'braintree-web/venmo';
import { create as createDataCollector } from 'braintree-web/data-collector';
import { PaymentError, UserPaymentError } from './error';
import logger from './log';

let braintreeClientInstance;
let fandangoToken;

export const getToken = () => fandangoToken;

const getCreatePaymentInstance = (paymentMethodId) => {
  switch (paymentMethodId) {
    case 'androidpay':
      return createGooglePayClient;
    case 'applepay':
      return createApplePayClient;
    case 'paypalonetouch':
      return createPayPalClient;
    case 'venmo':
      return createVenmoPayClient;
  }
  return null;
};

const walletApi = {
  SELECT: '/Wallets/Select',
  GET_CLIENT_TOKEN: '/Wallets/GetClientToken',
  GET_PAYMENT_BREAKDOWN: '/Wallets/GetLineItems',
  UPDATE_PAYMENT_INFO: '/Wallets/updatePaymentInfo',
};

const accountServiceApi = {
  GUEST_CHECKOUT: '/ExpressMobileAccountService/GuestCheckout',
};

const XHR_DEFAULT_TIMEOUT = 180 * 1000;

const log = logger('wallets.js');

/**
 * post
 *
 * Wrapper for making POST api calls to the various /Wallets endpoints. This helps standardize
 * how we handle errors and extract data from the responses.
 *
 * @param {Object} options
 * @param {String} options.url - The url of the api endpoint
 * @param {Object=} options.data - The data to use as the body of the api call
 * @param {Boolean=} options.async - Defaults to `true` but does allow to be explicitly set to `false`
 * @param {Boolean=} options.expectResponseData - Defaults to `true` but does allow to be explicitly set to `false`
 */
const post = async ({
  url,
  data,
  async,
  stringifyData,
  expectResponseData,
}) => {
  let error;
  let responseData;
  await $.ajax({
    type: 'POST',
    timeout: XHR_DEFAULT_TIMEOUT,
    url,
    contentType: stringifyData ? 'application/json' : undefined,
    data: stringifyData ? JSON.stringify(data) : data,
    dataType: 'json',
    async: async === undefined ? true : async,
    success: (res) => {
      responseData = _.get(res, 'data');
      const errorMessages = _.get(res, 'errors');

      if ((expectResponseData !== false && !responseData) || !_.isEmpty(errorMessages)) {
        log('post error (200)', { url, responseData, errorMessages });
        const errorStatus = _.get(res, 'Status');
        error = new UserPaymentError(errorMessages, errorStatus);
      }
    },
    error: (xhr) => {
      const errors = _.get(xhr, 'responseJSON.errors');
      log('post error', { url, data, errors });
      error = new PaymentError(errors);
    },
  });
  if (error) throw error;

  return responseData;
};

/**
 * getFandangoToken
 *
 * Generates a token to use to create a braintree client instance
 *
 * @async
 *
 * @returns {Promise<String>} token - The fandango token that can be used to generate a braintree instance
 * @throws {PaymentError} An error is thrown if a fandango token was unable to be generated
 */
const getFandangoToken = async () => {
  const data = await post({ url: walletApi.GET_CLIENT_TOKEN });

  if (!data.token) throw new PaymentError('No fandango token retrieved');

  return data.token;
};

/**
 * select
 *
 * Saves the provided payment method as the selection for the user's session
 *
 * @async
 * @param {String} paymentMethodId - The id of the selected payment method
 *
 * @throws {PaymentError} An error is thrown if the method was not selected successfully.
 */
const select = async (paymentMethodId) => {
  if (!paymentMethodId) throw new PaymentError('No payment method id provided');

  const data = await post({
    url: walletApi.SELECT,
    data: { walletId: paymentMethodId },
  });

  if (data.selected !== paymentMethodId) throw new PaymentError(`The provided payment method id (${paymentMethodId}) is not selected: ${data.selected}`);
};

/**
 * init
 *
 * Preemptively initializes a braintree client instance for use downstream so it is already accessible when needed.
 * If this is called and the Braintree instance has already been created, then nothing is done.
 *
 * @async
 *
 * @throws {PaymentError} - If the fandango token or Braintree client are unable to be created then a PaymentError is thrown
 */
export const init = async () => {
  if (!braintreeClientInstance) {
    fandangoToken = await getFandangoToken();
    braintreeClientInstance = await createBraintreeClient({ authorization: fandangoToken });
  }
};

/**
 * getBraintreeInstance
 *
 * Generates a Braintree instance to be used downstream for a specific payment method
 *
 * @async
 * @param {Object} options
 * @param {String} options.paymentMethodId - The id of the selected payment method
 * @param {Object=} options.config - Additional configuration needed to create the wallet
 * specific Braintree instrance. (NOTE: This is primarily used by Venmo)
 *
 * @returns {Promise<Object>} The Braintree instance for the provided payment method
 * @throws {PaymentError} An error is thrown if a Braintree instance specific for
 * the provied wallet cannot be returned for any reason. Reasons for this could include:
 * an invalid payment method id was provided, Braintree threw an error, etc.
 */
export const getBraintreeInstance = async ({ paymentMethodId, config }) => {
  if (!paymentMethodId) throw new PaymentError('No payment method id provided');

  const isCreditCard = paymentMethodId === 'creditcard';
  const createPaymentInstance = getCreatePaymentInstance(paymentMethodId);
  if (!isCreditCard && !createPaymentInstance) throw new PaymentError(`Unable to create a Braintree instance for ${paymentMethodId}`);

  try {
    if (!braintreeClientInstance) await init();

    return isCreditCard
      ? braintreeClientInstance
      : await createPaymentInstance({ client: braintreeClientInstance, ...config });
  } catch (err) {
    throw PaymentError(err);
  }
};

/**
 * getDeviceData
 *
 * Retrieves device data from Braintree. (NOTE: This is primarily used by Venmo)
 *https://braintree.github.io/braintree-web/3.19.0/DataCollector.html
 *
 * @async
 * @returns {String} JSON string to pass with server transactions
 * @throws {PaymentError} An error will be thrown if we are unable to retrieve the device data.
 */
export const getDeviceData = async () => {
  try {
    const dataCollectorInstance = await createDataCollector({
      client: braintreeClientInstance,
      paypal: true,
    });
    return dataCollectorInstance.deviceData;
  } catch (err) {
    throw new PaymentError(err);
  }
};

/**
 * getPaymentBreakdown
 *
 * Retrieves line items for the purchase to be used when authorizing the payment method for the transaction
 * NOTE: This is primarily used by Apple Pay and Buy Now PayPal
 *
 * @async
 * @param {String} paymentMethodId
 * @param {Boolean} buyNow
 *
 * @throws {PaymentError} An error is thrown if the line items for the transaction cannot be retrieved
 */
export const getPaymentBreakdown = async (paymentMethodId, buyNow) => {
  if (!paymentMethodId) throw new PaymentError('No payment method id provided');

  const data = await post({
    url: walletApi.GET_PAYMENT_BREAKDOWN,
    data: {
      walletId: paymentMethodId,
      payload: { buyNow },
    },
    async: false, // otherwise ApplePaySession wouldn't initiate because it's a user-gesture-required API
    stringifyData: true,
  });

  if (_.isEmpty(data.lineItems)) throw new PaymentError('No line items were retrieved');

  return data.lineItems;
};

/**
 * selectPaymentMethod
 *
 * Selects the payment method and additionally (when specified) also creates and returns a Braintree instance for the payment method.
 *
 * @async
 * @param {Object} options
 * @param {String} options.paymentMethodId - The id for the payment method
 * @param {Boolean=} options.initializeBraintree - Flag to identify if we should initialize a Braintree instance
 *
 * @returns {Object} response
 *          {String} response.paymentMethodId - The id for the selected payment method
 *          {Object=} response.braintreeInstance - The newly generated Braintree instance for the provided payment method
 *          {String=} response.fandangoToken - The fandango token used to initialize the Braintree instance
 * @throws
 */
export const selectPaymentMethod = async ({ paymentMethodId, initializeBraintree }) => {
  const response = { paymentMethodId };
  await select(paymentMethodId);

  if (initializeBraintree) {
    response.braintreeInstance = await getBraintreeInstance({ paymentMethodId });
  }
  response.fandangoToken = fandangoToken;

  return response;
};

/**
 * updateGuestEmail
 *
 * Updates the session payment context with the provided guest email address
 * NOTE: This is primarily used by Apple Pay and hits a different controller than the WalletsController
 *
 * @async
 * @param {String} email
 *
 * @throws {PaymentError} An error is thrown if no email address is provided.
 */
export const updateGuestEmail = async (email) => {
  if (!email) throw new PaymentError('No email address provided');

  await post({
    url: `${accountServiceApi.GUEST_CHECKOUT}?a=1`,
    data: { email },
    expectResponseData: false,
  });
};

/**
 * updatePaymentInfo
 *
 * Updates the session payment information with the provided Braintree nonce
 *
 * @async
 * @param {Object} options
 * @param {String} options.braintreeNonce
 * @param {String} options.walletId - The id for the selected wallet (payment method)
 * @param {String=} options.emailAddress - Optional email address to set on purchase context
 * @param {String=} options.deviceData - Device information retrieved by `getDeviceData`
 * @param {String=} options.postalCode - Transaction postal code
 *
 * @throws
 */
export const updatePaymentInfo = async ({
  braintreeNonce,
  walletId,
  emailAddress,
  deviceData,
  postalCode,
}) => {
  if (!braintreeNonce || !walletId) {
    throw new PaymentError('Unable to update payment info as either the nonce or id is missing');
  }

  const promises = [];

  promises.push(post({
    url: walletApi.UPDATE_PAYMENT_INFO,
    data: {
      walletId,
      payload: {
        nonce: braintreeNonce,
        deviceData,
        postalCode,
      },
    },
    stringifyData: true,
  }));

  if (emailAddress && emailAddress !== Commerce.state.account.emailAddress) {
    promises.push(updateGuestEmail(emailAddress));
    Commerce.state.account.emailAddress = emailAddress;
  }

  const [data] = await Promise.all(promises);
  if (!data.canCompletePurchase) throw new PaymentError('canCompletePurchase is not truthy');
};

export const getAppAuthorizationUrl = (walletId, token, purchaseTotal) => {
  // TODO: FIX -- 1) do not call Commerce.Helpers 2)is there a more elegant solution 3) can we just pass the decimal instead of a pennies string ??
  const total = Commerce.Helpers.toAppCurrencyString(purchaseTotal);
  // eslint-disable-next-line prefer-template
  return 'fandango://?&op=purchasewithwallet'
    + '&walletid=' + walletId
    + '&btclienttoken=' + encodeURIComponent(token)
    + '&timeoutsecs=180'
    + '&timeoutmsg=' + encodeURIComponent('A communication error has occurred. Please check your purchase history to confirm that your purchase was successfully completed.')
    + '&transpendingmsg=' + encodeURIComponent('Your transaction is currently being processed and cannot be cancelled at this time.')
    + '&generrmsg=' + encodeURIComponent('Sorry, there was a problem processing your order.')
    + '&linecount=3'
    + '&linename1='
    + '&linevalue1='
    + '&linename2='
    + '&linevalue2='
    + '&linename3=Fandango'
    + '&linevalue3=' + total
    + '&grandtotalname=Fandango'
    + '&grandtotalvalue3=' + total;
};
export const getAppAuthorizationFailureUrl = (walletId) => `fandango://?op=purchasewithwalletended&walletid=${walletId}&status=failure`;

export default init;
